import { Component, computed, inject, signal } from '@angular/core'
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatExpansionModule } from '@angular/material/expansion'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner'
import { MatSelectModule } from '@angular/material/select'
import { MatTooltip } from '@angular/material/tooltip'
import { API_URL } from '@core/constants/constant.data'
import { asyncError, asyncLoading, AsyncState, asyncSuccess } from '@core/models/async-state.model'
import { CustomSymbology } from '@core/models/custom-symbology.model'
import {
  AllModelScenarioDefinition,
  ScenarioExplorerPayload,
} from '@core/models/scenario-tab.model'
import { SessionManagerService } from '@core/services/session-manager.service'
import { CardLayerScenarioComponent } from './card-layer-scenario/card-layer-scenario.component'
import { ScenarioService } from './scenario.service'

@Component({
  selector: 'app-scenario-tab',
  templateUrl: './scenario-tab.component.html',
  styleUrl: './scenario-tab.component.scss',
  imports: [
    // Components
    CardLayerScenarioComponent,
    // Form
    FormsModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    // Material
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatIconModule,
    MatCardModule,
    MatTooltip,
    MatProgressSpinnerModule,
    MatExpansionModule,
  ],
})
export class ScenarioTabComponent {
  readonly sessionManagerService = inject(SessionManagerService)

  private readonly _scenarioService = inject(ScenarioService)

  private readonly _baseUrl = API_URL + '/scenario-explorer'

  isLoggedIn$ = this.sessionManagerService.isLoggedIn$

  models$ = this._scenarioService.models$
  scenarioExplorerPayload$ = this._scenarioService.scenarioExplorerPayload$
  scenarioResults$ = this._scenarioService.scenarioResults$

  isDataLoading$ = this._scenarioService.isDataLoading$

  hasInternalAccessState$ = signal<AsyncState<boolean>>(asyncLoading())
  scenarioAccessForm = new FormGroup({
    password: new FormControl(),
  })

  legendContent$ = signal<CustomSymbology[]>([])

  availableModels$ = computed<string[]>(() => {
    const models = this.models$()
    return models ? Object.keys(models) : []
  })

  availableScenarios$ = computed<string[]>(() => {
    const model = this.scenarioExplorerPayload$().model
    const modelData = this.models$()?.[model]
    return modelData ? Object.keys(modelData) : []
  })

  availableVariables$ = computed<string[]>(() => {
    const { model, scenario } = this.scenarioExplorerPayload$()
    const scenarioData = this.models$()?.[model]?.[scenario]
    return scenarioData ? Object.keys(scenarioData) : []
  })

  availableRegions$ = computed<string[]>(() => {
    const { model, scenario, variable } = this.scenarioExplorerPayload$()
    const variableData = this.models$()?.[model]?.[scenario]?.[variable]
    return variableData?.regions ?? []
  })

  availableYears$ = computed<number[]>(() => {
    const { model, scenario, variable } = this.scenarioExplorerPayload$()
    const variableData = this.models$()?.[model]?.[scenario]?.[variable]

    return variableData?.years
      ? Object.keys(variableData.years)
          .map(Number)
          .sort((a, b) => a - b)
      : []
  })

  availableSubannuals$ = computed<string[] | null>(() => {
    const { model, scenario, variable, year } = this.scenarioExplorerPayload$()

    const subannuals =
      year !== '' ? (this.models$()?.[model]?.[scenario]?.[variable]?.years?.[year] ?? null) : null

    return JSON.stringify(subannuals) === '["Year"]' ? null : subannuals
  })

  async ngOnInit() {
    if (this._scenarioService.models$() !== null) {
      this.hasInternalAccessState$.set(asyncSuccess(true))
      return
    }

    if (this.isLoggedIn$()) {
      await this._fetchAllModelScenarioDefinition()
    }
  }

  async getScenarioExplorerResultData() {
    this.isDataLoading$.set(true)

    const response = await this._scenarioService.getResult(this.scenarioExplorerPayload$())
    this.isDataLoading$.set(false)
    if (response === null) return

    this._scenarioService.addNewLayer(response)
  }

  async submitInternalScenarioPassword() {
    const formValue = this.scenarioAccessForm.value
    const passwordPayload = { internal_password: formValue.password }

    const response = await this._scenarioService.submitInternalScenarioPassword(passwordPayload)
    if (response === null) {
      return
    }
    this._fetchAllModelScenarioDefinition()
  }

  // Event handlers with cascade reset and auto-selection
  onModelChange(value: string) {
    if (!value) {
      this.scenarioExplorerPayload$.set({
        model: '',
        scenario: '',
        variable: '',
        region: '',
        year: '',
        subannual: '',
      })
      return
    }

    const models = this.models$()
    if (!models) return

    const scenarios = Object.keys(models[value])
    const firstScenario = scenarios[0] || ''

    let newSelection: ScenarioExplorerPayload = {
      model: value,
      scenario: firstScenario,
      variable: '',
      region: '',
      year: '',
      subannual: '',
    }

    if (firstScenario) {
      const scenarioData = models[value][firstScenario]
      const variables = Object.keys(scenarioData)
      const firstVariable = variables[0] || ''

      newSelection.variable = firstVariable

      if (firstVariable) {
        const variableData = scenarioData[firstVariable]
        newSelection.region = variableData.regions[0] || ''
        newSelection.year = Object.keys(variableData.years)
          .map(Number)
          .sort((a, b) => a - b)[0]
        newSelection.subannual = variableData.years[newSelection.year][0]
      }
    }

    this.scenarioExplorerPayload$.set(newSelection)
  }

  onScenarioChange(value: string) {
    const { model } = this.scenarioExplorerPayload$()
    const models = this.models$()
    if (!model || !value || !models) return

    const scenarioData = models[model][value]
    const variables = Object.keys(scenarioData)
    const firstVariable = variables[0] || ''

    let update: Partial<ScenarioExplorerPayload> = {
      scenario: value,
      variable: firstVariable,
      region: '',
      year: '',
    }

    if (firstVariable) {
      const variableData = scenarioData[firstVariable]
      update.region = variableData.regions[0] || ''
      update.year = Object.keys(variableData.years)
        .map(Number)
        .sort((a, b) => a - b)[0]
      update.subannual = variableData.years[update.year][0]
    }

    this.scenarioExplorerPayload$.update((current) => ({
      ...current,
      ...update,
    }))
  }

  onVariableChange(value: string) {
    const { model, scenario } = this.scenarioExplorerPayload$()
    const models = this.models$()
    if (!model || !scenario || !value || !models) return

    const variableData = models[model][scenario][value]

    this.scenarioExplorerPayload$.update((current) => ({
      ...current,
      variable: value,
      region: variableData?.regions[0] || '',
      year: Object.keys(variableData.years)
        .map(Number)
        .sort((a, b) => a - b)[0],
    }))
  }

  onRegionChange(value: string) {
    this.scenarioExplorerPayload$.update((current) => ({
      ...current,
      region: value,
    }))
  }

  onYearChange(value: number | '') {
    this.scenarioExplorerPayload$.update((current) => ({
      ...current,
      year: value,
    }))
  }

  onSubannualChange(value: string | '') {
    this.scenarioExplorerPayload$.update((current) => ({
      ...current,
      subannual: value,
    }))
  }

  private async _fetchAllModelScenarioDefinition() {
    this.hasInternalAccessState$.set(asyncLoading())
    const response = await this._scenarioService.GET(this._baseUrl + '/models')

    if (response === null) {
      this.hasInternalAccessState$.set(asyncError())
      return

      // We need this extra condition to remove a route that only check if the users is allowed or not. If not allowed return "{status: 'Access denied.', hasAccess: false}"
    } else if (response.hasAccess == false) {
      this.hasInternalAccessState$.set(asyncSuccess(false))
      return
      // if allowed return the models
    } else {
      this.hasInternalAccessState$.set(asyncSuccess(true))
      const definition = response as AllModelScenarioDefinition
      this.models$.set(definition)

      const firstModel = Object.keys(definition)[0]
      if (firstModel) {
        this.onModelChange(firstModel)
      }
    }
  }
}
